<template>
  <div class="form" :class="nftLayout">
    <div class="form__content">
      <IdentityQueueStepCounter />
      <h1 class="extra-large-headline">
        {{ $t("PageTitle.headline.identityConfirmationTitle") }}
      </h1>
    </div>
    <form @submit.prevent>
      <div class="form__content-wrapper">
        <FormInputField
          type="text"
          name="givenName"
          :inputValue="form.givenName"
          :label="$t('FormInputField.label.givenName')"
          :placeholder="$t('FormInputField.placeholder.givenName')"
          :errors="store.getErrorByField('givenName')"
          v-model:givenName="form.givenName"
        />
        <FormInputField
          v-if="!orderStore.isNFTMode"
          type="text"
          name="middleName"
          :inputValue="form.middleName"
          :label="$t('FormInputField.label.middleName')"
          :placeholder="$t('FormInputField.placeholder.middleName')"
          v-model:middleName="form.middleName"
        />
        <FormInputField
          name="surname"
          :label="$t('FormInputField.label.lastName')"
          :placeholder="$t('FormInputField.placeholder.lastName')"
          type="text"
          :inputValue="form.surname"
          v-model:surname="form.surname"
        />
        <FormInputField
          name="dob"
          placeholder="DD-MM-YYYY"
          type="text"
          mask="##-##-####"
          :inputValue="form.dob"
          :errors="store.getErrorByField('dob')"
          :label="$t('FormInputField.label.dob')"
          v-model:dob="form.dob"
        />

        <FormInputField
          class="form__addressLine1"
          name="addressLine1"
          type="text"
          iconType="MapPinCircle"
          :inputValue="form.addressLine1"
          :label="$t('FormInputField.label.addressLine1')"
          :placeholder="$t('FormInputField.placeholder.addressLine1')"
          v-model:addressLine1="form.addressLine1"
        />

        <FormInputField
          name="suburb"
          type="text"
          :inputValue="form.suburb"
          :placeholder="$t('FormInputField.placeholder.suburb')"
          :label="$t('FormInputField.label.suburb')"
          v-model:suburb="form.suburb"
        />

        <!-- may need to revist on more complex country requirements -->
        <FormSelect
          v-if="showFieldsOnUSIdentity"
          type="text"
          name="usStates"
          :defaultItem="defaultUSState"
          :label="$t('FormSelect.label.usStates')"
          :placeholder="$t('FormInputField.placeholder.state')"
          :items="createUSStateItemList"
          @dropdownValueUpdate="updateDropdown"
        />

        <FormInputField
          v-else
          v-model:state="form.state"
          name="state"
          type="text"
          :inputValue="form.state"
          :label="$t('FormInputField.label.state')"
          :placeholder="$t('FormInputField.placeholder.state')"
        />

        <FormInputField
          v-if="showFieldsOnUSIdentity"
          name="taxId"
          label="SSN*"
          placeholder="###-##-####"
          type="text"
          mask="###-##-####"
          :inputValue="form.taxId"
          :errors="store.getErrorByField('taxId')"
          v-model:taxId="form.taxId"
        />
        <FormInputField
          v-model:postcode="form.postcode"
          type="text"
          name="postcode"
          :label="$t('FormInputField.label.postcode')"
          :placeholder="$t('FormInputField.placeholder.postcode')"
          :inputValue="form.postcode"
        />
        <AutocompleteSelect
          hasIcon
          v-model:autocomplete="form.country"
          :type="'text'"
          :items="countryList"
          :label="$t('Autocomplete.label.countryResidence')"
          :placeholder="$t('MobileAutocompleteSelect.placeholder.phoneCountry')"
          :isLoading="isFetchingData"
        />

        <OccupationField
          v-if="showFieldsOnCAIdentity"
          name="occupation"
          @inputValueUpdate="updateOccupation"
        />

        <Checkbox
          v-if="showFieldsOnUSIdentity"
          name="tnc"
          :text="
            $t('Checkbox.text.zeroHash', {
              zeroHashLink: `<a target='_blank' href='https://www.zerohash.com/banxa'>${$t(
                'Checkbox.text.zeroHashUserAgreement',
              )}</a>`,
              zerohashLink: `<a target='_blank' href='https://zerohash.zendesk.com/hc/en-us/articles/360009900494-Privacy-Policy-'>${$t(
                'Checkbox.text.zeroHashPrivacyPolicy'
              )}</a>`,
              zendDeskLink: `<a href='https://zerohash.zendesk.com/hc/en-us/articles/360008819314-State-Money-Transmitter-Disclosures' target='_blank'>${$t(
                'Checkbox.text.regulatoryDisclosure'
              )}</a>`,
            })
          "
          v-model:value="form.tnc"
          v-model:tnc="form.tnc"
          :errors="store.getErrorByField('tnc')"
        />
      </div>

      <ButtonBase
        :isDisabled="store.formHasErrors || globalStore.getIsFormLoading"
        :isLoading="globalStore.getIsFormLoading"
        @onButtonClick="handleFormSubmit"
      >
        {{ $t("ButtonBase.label.submitMyDetails") }}
      </ButtonBase>
      <IdentityQueueNext />
    </form>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useGlobalStore } from '@/stores/globalStore';
import { useVerificationStore } from '@/stores/forms/verificationFormStore';
import { useIdentityQueueStore } from '@/stores/identityQueueStore';
import { useIdentityConfirmationStore } from '@/stores/forms/identityConfirmationFormStore';
import { useOccupationStore } from '@/stores/forms/occupationFormStore';
import { useOrderFormStore } from '@/stores/forms/orderFormStore';

import useApiHelpers from '@/composables/api/helpers';
import useSettingsEndpoints from '@/composables/api/useSettingsEndpoints';
import useWatchAndUpdate from '@/composables/helpers/useWatchAndUpdate';
import useIdentityCountryMixin from '@/composables/watchers/useIdentityCountryMixin';
import useGlobalCountriesActions from '@/composables/store/global/useGlobalCountriesActions';
import useCustomerActions from '@/composables/api/useCustomerActions';

import FormInputField from '@/components/ui/inputs/FormInputField.vue';
import FormSelect from '@/components/ui/selects/FormSelect.vue';
import AutocompleteSelect from '@/components/ui/selects/AutocompleteSelect.vue';
import ButtonBase from '@/components/ui/buttons/ButtonBase.vue';
import Checkbox from '@/components/ui/inputs/Checkbox.vue';
import OccupationField from '@/components/ui/inputs/OccupationField.vue';
import IdentityQueueStepCounter from '@/components/layout/progress/IdentityQueueStepCounter.vue';
import IdentityQueueNext from '@/components/layout/progress/IdentityQueueNext.vue';

const route = useRoute();
const router = useRouter();

const globalStore = useGlobalStore();
const verificationStore = useVerificationStore();
const store = useIdentityConfirmationStore();
const identityQueueStore = useIdentityQueueStore();
const orderStore = useOrderFormStore();

const { postRequest } = useApiHelpers();
const { getRestrictedUSStatesSettings } = useSettingsEndpoints();
const { watchAndUpdateStore } = useWatchAndUpdate();
const { watchCountryAndUpdateStoreHandler } = useIdentityCountryMixin();
const { fetchCountries } = useGlobalCountriesActions();
const { fetchCustomerKycSharingInfo, fillForm } = useCustomerActions();

const countryList = ref([]);
const stateList = ref([]);
const defaultUSState = ref('');
const disabledStatesList = ref([]);
const form = ref({
  givenName: store.form.givenName.value || '',
  middleName: store.form.middleName.value || '',
  surname: store.form.surname.value || '',
  dob: store.form.dob.value || '',
  addressLine1: store.form.addressLine1.value || '',
  suburb: store.form.suburb.value || '',
  state: store.form.state.value || '',
  postcode: store.form.postcode.value || '',
  taxId: store.form.taxId.value || '',
  tnc: false,
  occupation: store.form.occupation.value || '',
  country: verificationStore.form.country?.value || '',
});
const isFetchingData = ref(true);
const nftLayout = computed(() => (orderStore.isNFTMode ? 'nft-layout' : ''));

// fixes the US state dropdown not correctly showing the state value
// or accounting for the disabled state from the kyc sharing
const getStateUSNameByNameOrCode = (state) => {
  if (!state) {
    return '';
  }

  const stateToFind = state.toLowerCase();

  const findStateCode = createUSStateItemList.value.find(
    (usState) => usState.name.toLowerCase() === stateToFind
      || usState.value.toLowerCase() === stateToFind,
  )?.value;

  const foundState = createUSStateItemList.value.find(
    ({ name, value }) => (stateToFind === name.toLowerCase()
        || stateToFind === value.toLowerCase())
      && !disabledStatesList.value.includes(findStateCode)
  );

  return foundState ? foundState.name : "";
};

const getUSStatesData = async () => {
  const {
    data: { acceptedStates, restrictedStates },
  } = await getRestrictedUSStatesSettings();
  const restrictedStateCodes = Object.keys(restrictedStates);

  stateList.value = {
    ...acceptedStates,
    ...restrictedStates,
  };
  disabledStatesList.value = restrictedStateCodes;
  // populates the initial state from KYC sharing
  defaultUSState.value = getStateUSNameByNameOrCode(form.value.state);
};

const showFieldsOnUSIdentity = computed(
  () => form.value.country && form.value.country.toLowerCase() === 'united states'
);

const showFieldsOnCAIdentity = computed(
  () => form.value.country && form.value.country.toLowerCase() === 'canada'
);

const createUSStateItemList = computed(() => {
  const states = Object.entries(stateList.value);
  if (states.length === 0) return [];
  return states.map(([stateCode, stateName]) => ({
    name: stateName,
    value: stateCode,
    isDisabled: disabledStatesList.value.includes(stateCode),
  }));
});
const updateDropdown = (dropdownData) => {
  form.value.state = Object.values(dropdownData)[0]?.value;
};

const updateOccupation = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  form.value[field] = value;
};

const handleFormSuccess = () => {
  // we need to recall the queue as it can be overriden
  // by new requirements based on the country select
  identityQueueStore.updateToNextRoute();
  identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
    router.push(nextRoute);
  });
};

const handleFormSubmit = () => {
  const { country, ...rest } = form.value;
  const countryCode = globalStore.getCountryCode(country);
  globalStore.setIsFormLoading(true);
  if (countryCode !== 'US') {
    postRequest(
      'customers',
      { country: countryCode, ...rest },
      handleFormSuccess,
    );
    return;
  }
  // Post taxState for US customer
  postRequest(
    'customers',
    { country: countryCode, taxState: form.value.state, ...rest },
    handleFormSuccess,
  );
};

onMounted(async () => {
  countryList.value = await fetchCountries();
  const personalDetails = await fetchCustomerKycSharingInfo();
  fillForm(personalDetails, form, store);
  await getUSStatesData();
  isFetchingData.value = false;
});

watchAndUpdateStore(form, store, () => {});
watchCountryAndUpdateStoreHandler(form, store, () => {});
</script>

<style lang="scss" scoped>
.form {
  &__content-wrapper {
    display: flex;
    flex-wrap: wrap;

    .base-input,
    .occupation-field,
    .base-form-select,
    .autocomplete-select,
    .base-field {
      flex-grow: 1;
      padding-inline: 0.5rem;

      @include breakpoint($min-l) {
        &:not(.form__addressLine1) {
          width: 50%;
        }
      }
    }
  }
}

.nft-layout {
  .base-input,
  .occupation-field,
  .base-form-select,
  .autocomplete-select,
  .base-field {
    &:not(.form__addressLine1) {
      width: 50%;
    }
  }
}
</style>
